"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { RefreshCcw, Home } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();

  const isRTL = locale === "ar";

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <div
      className={cn(
        "min-h-screen flex items-center justify-center px-6",
        "bg-gradient-to-br from-primary/10 via-background to-secondary/10",
        isRTL && "rtl",
      )}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="relative w-full max-w-lg rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl p-10 text-center animate-in fade-in zoom-in-95 duration-500">
        {/* Glow Effects */}
        <div className="absolute -top-10 -left-10 w-40 h-40 bg-primary/30 blur-3xl rounded-full opacity-40" />
        <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-secondary/30 blur-3xl rounded-full opacity-40" />

        {/* Error Code */}
        <h1 className="text-7xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
          500
        </h1>

        {/* Title */}
        <h2 className="mt-4 text-2xl font-semibold">{t("errorTitle")}</h2>

        {/* Description */}
        <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
          {t("errorDescription")}
        </p>

        {/* Buttons */}
        <div className="mt-8 flex gap-4 justify-center">
          <button
            onClick={reset}
            className="cursor-pointer flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white hover:opacity-90 transition-all duration-200 shadow-lg"
          >
            <RefreshCcw size={16} />
            {t("tryAgain")}
          </button>

          <button
            onClick={() => router.push("/")}
            className="cursor-pointer flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border bg-background hover:bg-accent transition-all duration-200"
          >
            <Home size={16} />
            {t("goHome")}
          </button>
        </div>
      </div>
    </div>
  );
}
